/**
 * Tablero de la cápsula de Chisilandia (snippets/chz-consola-tablero.liquid): cuatro fichas que tiran
 * el dado y avanzan solas por "Isla Chisilandia".
 * - Azul suma 3 monedas, rojo resta 3 y verde es sorpresa (5 para arriba o para abajo).
 * - Quien pasa por la casilla del queso con 10 monedas o más se lleva un queso.
 * - En la ronda 20 gana quien tenga más quesos (y monedas, si empatan) y se vuelve a empezar.
 * Corre solo mientras se ve: se pausa fuera de pantalla, con la pestaña oculta y con las animaciones
 * apagadas (html[data-chz-motion='off']). Con movimiento reducido queda quieto desde el principio.
 *
 * Personaje que cae (.chz-capsule[data-drop]): cuando el cuarto entra a la pantalla, la cápsula pasa a
 * is-dropping y el CSS hace la caída como hoja al viento y el polvo al llegar. Una vez por visita a la
 * página; en Sobre Chisilandia, como el cuarto ya se ve al entrar, cae apenas carga.
 */
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const motionOff = () => document.documentElement.dataset.chzMotion === 'off';

/** Giro del cubo que deja cada cara de frente, un poco ladeado para que se vea en 3D. */
const TILT = 'rotateX(-16deg) rotateY(20deg)';
const FACES = {
  1: TILT,
  2: `${TILT} rotateX(90deg)`,
  3: `${TILT} rotateY(-90deg)`,
  4: `${TILT} rotateY(90deg)`,
  5: `${TILT} rotateX(-90deg)`,
  6: `${TILT} rotateY(180deg)`,
};
const SPREAD = [3, 9, 14, 19];
const ROUNDS = 20;

class ChzBoard extends HTMLElement {
  connectedCallback() {
    if (this.spaces) return;
    try {
      this.spaces = JSON.parse(this.querySelector('[data-chz-board-spaces]')?.textContent || '[]');
    } catch {
      this.spaces = [];
    }
    if (this.spaces.length < SPREAD[3] + 1) return;

    this.pawns = [...this.querySelectorAll('.chz-board__pawn')];
    this.players = [...this.querySelectorAll('.chz-board__player')];
    this.dice = this.querySelector('.chz-board__dice');
    this.cube = this.querySelector('.chz-board__cube');
    this.pop = this.querySelector('.chz-board__pop');
    this.roundEl = this.querySelector('[data-round]');
    this.banner = this.querySelector('.chz-board__banner');
    this.cheeseAt = this.spaces.findIndex((space) => space[0] === 'c');
    this.stopped = false;
    this.visible = false;
    this.reset();

    this.onChange = () => this.update();
    this.io = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.update();
    });
    this.io.observe(this);
    document.addEventListener('visibilitychange', this.onChange);
    reduce.addEventListener?.('change', this.onChange);
    this.mo = new MutationObserver(this.onChange);
    this.mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-chz-motion'] });
  }

  disconnectedCallback() {
    this.stopped = true;
    this.io?.disconnect();
    this.mo?.disconnect();
    document.removeEventListener('visibilitychange', this.onChange);
    reduce.removeEventListener?.('change', this.onChange);
    this.wake?.();
  }

  get canRun() {
    return this.visible && !document.hidden && !reduce.matches && !motionOff();
  }

  update() {
    if (!this.canRun) return;
    if (!this.loop) this.loop = this.play();
    this.wake?.();
  }

  /** Espera ms de tiempo en marcha: si en el medio se pausa, sigue cuando vuelve a correr. */
  async wait(ms) {
    await new Promise((resolve) => window.setTimeout(resolve, ms));
    while (!this.canRun && !this.stopped) {
      await new Promise((resolve) => {
        this.wake = resolve;
      });
    }
  }

  reset() {
    this.pos = [...SPREAD];
    this.coins = [10, 10, 10, 10];
    this.cheese = [0, 0, 0, 0];
    this.round = 1;
    this.place();
    this.renderHud();
  }

  /** Pone cada ficha en su casilla; si comparten casilla, se acomodan lado a lado. */
  place() {
    const groups = new Map();
    this.pos.forEach((space, player) => groups.set(space, [...(groups.get(space) || []), player]));
    this.pawns.forEach((pawn, player) => {
      const [, x, y, ux] = this.spaces[this.pos[player]];
      const group = groups.get(this.pos[player]);
      const k = group.indexOf(player);
      const dx = (k - (group.length - 1) / 2) * ux * 0.85;
      pawn.style.setProperty('--x', String(x));
      pawn.style.setProperty('--y', String(y));
      pawn.style.setProperty('--ux', String(ux));
      pawn.style.setProperty('--dx', dx.toFixed(2));
      pawn.style.setProperty('--z', String(Math.round(y * 10) + k));
      pawn.dataset.dx = dx.toFixed(2);
    });
  }

  renderHud() {
    this.players.forEach((panel, player) => {
      const coins = panel.querySelector('[data-coins]');
      const cheese = panel.querySelector('[data-cheese]');
      if (coins) coins.textContent = String(this.coins[player]);
      if (cheese) cheese.textContent = String(this.cheese[player]);
    });
    if (this.roundEl) this.roundEl.textContent = String(this.round);
  }

  /** Punto sobre la ficha (para el dado y los números que saltan). @param {number} player */
  spot(player) {
    const [, x, y] = this.spaces[this.pos[player]];
    return [x + Number(this.pawns[player].dataset.dx || 0), y];
  }

  async play() {
    await this.wait(2600);
    let player = 0;
    while (!this.stopped) {
      await this.turn(player);
      player = (player + 1) % 4;
      if (player !== 0) continue;
      if (this.round >= ROUNDS) {
        await this.finish();
      } else {
        this.round += 1;
        this.renderHud();
      }
    }
  }

  /** @param {number} player */
  async turn(player) {
    this.players.forEach((panel, i) => panel.classList.toggle('is-turn', i === player));
    const [x, y] = this.spot(player);
    this.dice?.style.setProperty('--x', String(x));
    this.dice?.style.setProperty('--y', String(y));
    this.classList.add('is-rolling');
    await this.wait(950);

    const roll = 1 + Math.floor(Math.random() * 6);
    if (this.cube) this.cube.style.transform = FACES[roll];
    this.classList.replace('is-rolling', 'is-rolled');
    await this.wait(700);
    this.classList.remove('is-rolled');
    await this.wait(150);

    const piece = this.pawns[player].querySelector('.chz-board__piece');
    for (let step = 0; step < roll; step++) {
      this.pos[player] = (this.pos[player] + 1) % this.spaces.length;
      this.place();
      piece?.animate?.(
        [{ transform: 'translateY(0)' }, { transform: 'translateY(-34%)', offset: 0.45 }, { transform: 'translateY(0)' }],
        { duration: 300, easing: 'ease-in-out' }
      );
      await this.wait(330);
      if (this.pos[player] === this.cheeseAt && this.coins[player] >= 10) {
        this.coins[player] -= 10;
        this.cheese[player] += 1;
        this.renderHud();
        await this.popup(player, '+1', 'is-cheese');
      }
    }

    const type = this.spaces[this.pos[player]][0];
    let delta = 0;
    if (type === 'b') delta = 3;
    if (type === 'r') delta = -3;
    if (type === 'g') delta = Math.random() < 0.6 ? 5 : -5;
    if (delta) {
      this.coins[player] = Math.max(0, this.coins[player] + delta);
      this.renderHud();
      await this.popup(player, delta > 0 ? `+${delta}` : `−${-delta}`, delta > 0 ? 'is-up' : 'is-down');
    }
    await this.wait(380);
  }

  /** @param {number} player @param {string} text @param {string} tone */
  async popup(player, text, tone) {
    if (!this.pop) return;
    const [x, y] = this.spot(player);
    this.pop.textContent = text;
    this.pop.className = `chz-board__pop ${tone}`;
    this.pop.style.setProperty('--x', String(x));
    this.pop.style.setProperty('--y', String(y));
    this.pop.animate?.(
      [
        { opacity: 0, transform: 'translateY(40%) scale(0.6)' },
        { opacity: 1, transform: 'translateY(0) scale(1.15)', offset: 0.22 },
        { opacity: 1, transform: 'translateY(-25%) scale(1)', offset: 0.7 },
        { opacity: 0, transform: 'translateY(-70%) scale(1)' },
      ],
      { duration: 950, easing: 'ease-out' }
    );
    await this.wait(760);
  }

  async finish() {
    let best = 0;
    for (let p = 1; p < 4; p++) {
      const ahead = this.cheese[p] - this.cheese[best] || this.coins[p] - this.coins[best];
      if (ahead > 0) best = p;
    }
    if (this.banner) this.banner.textContent = (this.dataset.winner || '[jugador]').replace('[jugador]', `P${best + 1}`);
    this.players.forEach((panel, i) => panel.classList.toggle('is-turn', i === best));
    this.classList.add('is-banner');
    await this.wait(2800);
    this.classList.remove('is-banner');
    this.reset();
    await this.wait(600);
  }
}

if (!customElements.get('chz-board')) customElements.define('chz-board', ChzBoard);

/* ---------- Personaje que cae ---------- */
/** @param {ParentNode} root */
function setupDrop(root) {
  for (const capsule of root.querySelectorAll('.chz-capsule[data-drop]')) {
    if (!(capsule instanceof HTMLElement) || capsule.dataset.dropReady !== undefined) continue;
    capsule.dataset.dropReady = '';
    if (reduce.matches || motionOff() || !('IntersectionObserver' in window)) {
      capsule.classList.add('is-dropping', 'is-landed');
      continue;
    }
    const stage = capsule.querySelector('.chz-capsule__stage') || capsule;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        io.disconnect();
        capsule.classList.add('is-dropping');
      },
      { threshold: 0.35 }
    );
    io.observe(stage);
  }
}

setupDrop(document);
document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) setupDrop(event.target);
});
